import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole, VendorType } from '../types';
import './Login.css';

const Register: React.FC = () => {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: UserRole.ARCHITECT as UserRole,
    vendorType: VendorType.LABOUR as VendorType,
    company: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.phone || !formData.password) {
      setError('Name, phone, and password are required');
      return;
    }
    if (formData.phone.length !== 10) {
      setError('Phone number must be 10 digits');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const data: any = {
        name: formData.name,
        phone: formData.phone,
        password: formData.password,
        role: formData.role,
        company: formData.company || undefined,
      };
      if (formData.email) data.email = formData.email;
      if (formData.role === UserRole.VENDOR) data.vendorType = formData.vendorType;

      const result = await register(data);
      setSuccess(result.message);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="app-logo">
            <h2 className="logo-brand-small">arcivi</h2>
          </div>
          <div className="success-message">{success}</div>
          <div className="toggle-form">
            <Link to="/login" className="link-button">Back to Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="app-logo">
          <h2 className="logo-brand-small">arcivi</h2>
        </div>

        <h3 className="login-title">Create Account</h3>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="10-digit phone number"
              maxLength={10}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email (Optional)</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Minimum 6 characters"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter your password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="role">Role</label>
            <select id="role" name="role" value={formData.role} onChange={handleChange}>
              <option value={UserRole.ARCHITECT}>Architect</option>
              <option value={UserRole.CLIENT}>Client</option>
              <option value={UserRole.VENDOR}>Vendor</option>
              <option value={UserRole.ADMIN}>Admin</option>
            </select>
          </div>

          {formData.role === UserRole.VENDOR && (
            <div className="form-group">
              <label htmlFor="vendorType">Vendor Type</label>
              <select id="vendorType" name="vendorType" value={formData.vendorType} onChange={handleChange}>
                <option value={VendorType.LABOUR}>Labour Vendor</option>
                <option value={VendorType.MATERIAL}>Material Vendor</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="company">Company (Optional)</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Company name"
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>

        <div className="toggle-form">
          Already have an account?{' '}
          <Link to="/login" className="link-button">Sign In</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
